import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { SkeletonModule } from 'primeng/skeleton';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { OfertasService } from '../../services/OfertasService';
import { ActivatedRoute, Router } from '@angular/router';
import { OfertaDtoOut } from '../../models/OfertaDtoOut.model';
import { APP_ROUTES } from '../../../../core/constants/routes.const';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { TARIFAS } from '../../../../core/enums/tarifas.enum';
import { OfertaDtoIn } from '../../models/OfertaDtoIn.model';
import { InputNumber } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { ConfirmDialog } from 'primeng/confirmdialog';

@Component({
  selector: 'app-oferta-detalle',
  standalone: true,
  imports: [
    CommonModule,
    CardModule,
    ButtonModule,
    TagModule,
    SkeletonModule,
    ToastModule,
    ReactiveFormsModule,
    InputTextModule,
    InputNumber,
    DropdownModule,
    ConfirmDialog,
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './oferta-detalle.component.html',
  styleUrl: './oferta-detalle.component.scss',
})
export class OfertaDetalleComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly ofertasApi = inject(OfertasService);
  private readonly messages = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);

  readonly R = APP_ROUTES;
  readonly tarifas = [
    { label: TARIFAS.TD20, value: TARIFAS.TD20 },
    { label: TARIFAS.TD30, value: TARIFAS.TD30 },
  ];

  loading = signal(true);
  saving = signal(false);
  editMode = signal(false);

  oferta = signal<OfertaDtoOut | null>(null);
  form!: FormGroup;

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (isNaN(id)) {
      this.router.navigate([`/${APP_ROUTES.DASHBOARD}`]);
      return;
    }

    this.ofertasApi.getOfertaById(id).subscribe({
      next: (data) => {
        this.oferta.set(data);

        this.form = this.fb.group({
          nombreCliente: [data.nombreCliente],
          cups: [data.cups],
          companiaActual: [data.companiaActual],
          tipoTarifa: [data.tipoTarifa],
          diasFactura: [data.diasFactura],
          potenciaP1: [data.potenciaP1],
          potenciaP2: [data.potenciaP2],
          consumoP1: [data.consumoP1],
          consumoP2: [data.consumoP2],
          consumoP3: [data.consumoP3],
          precioConsumidoP1: [data.precioConsumidoP1],
          precioConsumidoP2: [data.precioConsumidoP2],
          precioConsumidoP3: [data.precioConsumidoP3],
          precioTotalConsumido: [{ value: data.precioTotalConsumido, disabled: true }],
          precioTotalPotencia: [data.precioTotalPotencia],
          otrosCostes: [data.otrosCostes],
          bonoSocial: [data.bonoSocial],
          regularizacionFinanciacionBs: [data.regularizacionFinanciacionBs],
          rdTopeGas: [data.rdTopeGas],
          descuento: [data.descuento],
          impuestoElectricidad: [data.impuestoElectricidad],
          alquilerDeEquipos: [data.alquilerDeEquipos],
          iva: [data.iva],
        });

        this.form.valueChanges.subscribe(() => {
          const p1 = this.form.get('precioConsumidoP1')?.value ?? 0;
          const p2 = this.form.get('precioConsumidoP2')?.value ?? 0;
          const p3 = this.form.get('precioConsumidoP3')?.value ?? 0;

          const total = p1 + p2 + p3;
          this.form.get('precioTotalConsumido')?.setValue(total, { emitEvent: false });
        });
      },
      error: () => {
        this.messages.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo cargar la oferta',
        });
        this.router.navigate([`/${APP_ROUTES.DASHBOARD}`]);
      },
      complete: () => this.loading.set(false),
    });
  }

  toggleEdit() {
    this.editMode.set(true);
  }

  cancelEdit() {
    this.editMode.set(false);
  }

  saveEdit() {
    if (!this.form.valid || !this.oferta()) return;
    this.saving.set(true);

    const dto: OfertaDtoIn = this.form.getRawValue() as OfertaDtoIn;

    this.ofertasApi.updateOferta(this.oferta()!.id, dto).subscribe({
      next: (data) => {
        this.oferta.set(data);
        this.messages.add({
          severity: 'success',
          summary: 'Guardado',
          detail: `La oferta #${data.id} fue actualizada.`,
        });
        this.editMode.set(false);
      },
      error: () => {
        this.messages.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo actualizar la oferta',
        });
      },
      complete: () => this.saving.set(false),
    });
  }

  confirmarEliminar() {
    if (!this.oferta()) return;

    this.confirmation.confirm({
      message: `¿Seguro que deseas eliminar la oferta #${this.oferta()!.id}?`,
      header: 'Confirmación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.eliminar(),
    });
  }

  eliminar() {
    this.saving.set(true);
    this.ofertasApi.deleteOfertaById(this.oferta()!.id).subscribe({
      next: () => {
        this.messages.add({
          severity: 'success',
          summary: 'Eliminada',
          detail: `La oferta fue eliminada correctamente.`,
        });
        this.router.navigate([`/${APP_ROUTES.DASHBOARD}`]);
      },
      error: () => {
        this.messages.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo eliminar la oferta',
        });
      },
      complete: () => this.saving.set(false),
    });
  }

  comparar() {
    if (!this.oferta()) return;
    this.router.navigate([`/${APP_ROUTES.OFERTAS.COMPARAR(this.oferta()!.id)}`]).then();
  }
}
